import { useMemo } from 'react';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { FiMoreVertical } from 'react-icons/fi';
import { LuLoader } from 'react-icons/lu';
import type { DropdownMenuOption, DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import { isBroadcastEditable, isBroadcastSent } from '../logic/broadcast.logic';

import EditIcon from '@/assets/icons/ic-edit-01.svg';
import DuplicateIcon from '@/assets/icons/ic-duplicate.svg';
import EyeIcon from '@/assets/icons/ic-eye-show.svg';
import TrashIcon from '@/assets/icons/ic-trash.svg';

interface BroadcastActionsMenuProps {
    broadcast: FlowSummary;
    onEdit: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onViewRecipients: () => void;
    isDuplicating?: boolean;
}

export const BroadcastActionsMenu = ({
    broadcast,
    onEdit,
    onDelete,
    onDuplicate,
    onViewRecipients,
    isDuplicating = false,
}: BroadcastActionsMenuProps) => {
    const isEditable = isBroadcastEditable(broadcast);
    const isSent = isBroadcastSent(broadcast);
    const hasSentCount = (broadcast.recipient_count ?? 0) > 0;

    const menuOptions: DropdownMenuOption[] = useMemo(() => {
        const options: DropdownMenuOption[] = [];

        // Edit option (only for editable broadcasts)
        if (isEditable) {
            options.push({
                label: 'Edit Email',
                value: 'edit',
                icon: EditIcon,
            });
        }

        // View recipients (only for sent broadcasts with recipients)
        if (isSent && hasSentCount) {
            options.push({
                label: 'View recipients',
                value: 'view-recipients',
                icon: EyeIcon,
            });
        }

        // Duplicate (always available)
        options.push({
            label: 'Duplicate',
            value: 'duplicate',
            icon: DuplicateIcon,
        });

        // Delete (only for editable broadcasts)
        if (isEditable) {
            // Add divider before delete
            options.push({
                label: '',
                value: 'divider-delete',
                type: 'divider',
            });

            options.push({
                label: 'Delete',
                value: 'delete',
                icon: TrashIcon,
                type: 'destructive',
            });
        }

        return options;
    }, [isEditable, isSent, hasSentCount]);

    const handleSelect = (value: DropdownValue) => {
        switch (value.value) {
            case 'edit':
                onEdit();
                break;
            case 'duplicate':
                onDuplicate();
                break;
            case 'view-recipients':
                onViewRecipients();
                break;
            case 'delete':
                onDelete();
                break;
        }
    };

    return (
        <DropdownProvider
            dropdownId={`broadcast-menu-${broadcast.flow_id}`}
            dropdownName={`broadcast-menu-${broadcast.flow_id}`}
        >
            <StanDropdownMenu
                id={`broadcast-menu-${broadcast.flow_id}`}
                name={`broadcast-menu-${broadcast.flow_id}`}
                options={menuOptions}
                placement='bottom-end'
                onSelect={handleSelect}
                currentValue={null}
                minWidth={176}
            >
                <button
                    className='text-gray/75 hover:text-dark hover:bg-neutral-hover/50 flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors'
                    disabled={isDuplicating}
                >
                    {isDuplicating ? (
                        <LuLoader className='size-5 animate-spin' />
                    ) : (
                        <FiMoreVertical className='size-5' />
                    )}
                </button>
            </StanDropdownMenu>
        </DropdownProvider>
    );
};
